/** Age units the child benefit-description bands use per bound. */
export type ChildAgeUnit = "days" | "months" | "years";

/**
 * Whole days per unit — the common denominator for comparing and displaying
 * child age bands (CD-1976). Bands persist in the analyst's chosen unit; this
 * table converts a bound to days to pick a display unit and to compare bounds
 * across units. A year is 12 months of 30 days each (360, not 365) so a value
 * entered as "12 months" lands exactly on the year boundary and renders as an
 * age rather than "12 months". Shared by the Present editor and the read/MP
 * formatters so the two never drift.
 */
export const DAYS_PER_CHILD_AGE_UNIT: Record<ChildAgeUnit, number> = {
  days: 1,
  months: 30,
  years: 360,
};

/**
 * The display unit for a day count: the largest unit the count is at least one
 * of — ≥360d → years, ≥30d → months, otherwise days (CD-1976).
 */
export const displayChildAgeUnitForDays = (days: number): ChildAgeUnit => {
  if (days >= DAYS_PER_CHILD_AGE_UNIT.years) return "years";
  if (days >= DAYS_PER_CHILD_AGE_UNIT.months) return "months";

  return "days";
};

const asChildAgeUnit = (unit: string | undefined): ChildAgeUnit =>
  unit === "months" || unit === "years" ? unit : "days";

const UNIT_LABEL: Record<"days" | "months", { one: string; many: string }> = {
  days: { one: "day", many: "days" },
  months: { one: "month", many: "months" },
};

/**
 * The day count expressed in its display unit, rounded to a whole number.
 * CD-1976 forbids decimal displays, so a count that doesn't divide evenly is
 * rounded (53 days → 2 months, 390 days → 1 year). This rounding is where the
 * read display and the exact stored value legitimately diverge.
 */
export const displayChildAgeValue = (days: number): number =>
  Math.round(days / DAYS_PER_CHILD_AGE_UNIT[displayChildAgeUnitForDays(days)]);

/**
 * Format a stored bound (value expressed in `storedUnit`) as a human label:
 * normalize to days, then express it in the largest whole unit it resolves to.
 * Years render as an age ("Age 1"); days and months render as a count with a
 * pluralized label ("6 months", "1 day"). Returns "" when the bound is absent.
 */
export const formatChildAgeBound = (
  value: number | undefined,
  storedUnit: string | undefined,
): string => {
  if (value == null) {
    return "";
  }

  const days = value * DAYS_PER_CHILD_AGE_UNIT[asChildAgeUnit(storedUnit)];
  const unit = displayChildAgeUnitForDays(days);
  const display = displayChildAgeValue(days);

  if (unit === "years") {
    return `Age ${display}`;
  }

  const label = display === 1 ? UNIT_LABEL[unit].one : UNIT_LABEL[unit].many;

  return `${display} ${label}`;
};
